<template>
    <div ref="container" id="container"></div>
</template>

<script setup>
import * as THREE from 'three'
import { fragmentShader, vertexShader } from '../shaders'
import { onMounted, onBeforeUnmount, ref } from 'vue'

const container = ref(null)

const TARGET_FPS = 30
const FRAME_BUDGET = 1000 / TARGET_FPS
const DPR_CAP = 1

let camera, scene, renderer, uniforms, material, geometry, mesh, clock
let rafId = 0
let lastFrame = 0
let resizeRAF = 0
let paused = false

const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

function init(el) {
    camera = new THREE.Camera()
    camera.position.z = 1
    scene = new THREE.Scene()
    uniforms = {
        color1: { value: new THREE.Color(0xffffff) },
        color2: { value: new THREE.Color(0xffffff) },
        color3: { value: new THREE.Color(0xffffff) },
        iterations: { value: 1 },
        permutations: { value: 5 },
        brightness: { value: 0.1 },
        time: { value: 0 },
        speed: { value: 0.002 },
        uvScale: { value: new THREE.Vector2(0.2, 0.2) }
    }
    material = new THREE.RawShaderMaterial({ uniforms, vertexShader, fragmentShader })
    geometry = new THREE.PlaneGeometry(2, 2)
    mesh = new THREE.Mesh(geometry, material)
    scene.add(mesh)

    renderer = new THREE.WebGLRenderer({
        antialias: false,
        powerPreference: 'low-power',
        alpha: false
    })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, DPR_CAP))
    renderer.setSize(window.innerWidth, window.innerHeight)
    el.appendChild(renderer.domElement)
}

function render() {
    uniforms.time.value = clock.getElapsedTime() * 60
    renderer.render(scene, camera)
}

function loop(now) {
    rafId = requestAnimationFrame(loop)
    if (paused) return
    if (now - lastFrame < FRAME_BUDGET) return
    lastFrame = now
    render()
}

function onResize() {
    if (!renderer) return
    if (resizeRAF) cancelAnimationFrame(resizeRAF)
    resizeRAF = requestAnimationFrame(() => {
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, DPR_CAP))
        renderer.setSize(window.innerWidth, window.innerHeight)
    })
}

function onVisibility() {
    paused = document.hidden
    if (!paused) lastFrame = 0
}

onMounted(() => {
    if (window.innerWidth <= 800) return
    init(container.value)
    clock = new THREE.Clock()

    if (reducedMotion) {
        render()
        return
    }

    rafId = requestAnimationFrame(loop)
    window.addEventListener('resize', onResize, { passive: true })
    document.addEventListener('visibilitychange', onVisibility)
})

onBeforeUnmount(() => {
    if (rafId) cancelAnimationFrame(rafId)
    if (resizeRAF) cancelAnimationFrame(resizeRAF)
    window.removeEventListener('resize', onResize)
    document.removeEventListener('visibilitychange', onVisibility)
    geometry?.dispose()
    material?.dispose()
    renderer?.dispose()
    renderer?.domElement?.parentNode?.removeChild(renderer.domElement)
})
</script>

<style lang="scss">
#container {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: -1;
}
</style>
